import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LayoutContext } from '../../contexts/layout';
import { getIsFormItemDisabled } from '../../helpers/form';
import { useOnValueChangeAction } from '../../hooks/form/useOnValueChangeAction';
import {
    pushFormFieldTitle, pushFormFieldValidation, setFormFieldLayout, setFormFieldValue,
} from '../../redux/actions/forms';
import {
    formEditedFieldsSelector, formFieldMessageSelector, formItemValidationResult, getFormFieldValue,
} from '../../redux/selectors/forms';
import { IFormItemProps } from '../../types/components/form';
import { IFormItemWrapperProps } from '../../types/components/form-item-wrapper';

function FormItemWrapper(props: React.PropsWithChildren<IFormItemWrapperProps>) {
    const {
        children, fieldName, formMode, validation, fieldTitle, layout,
    } = props;

    const { layoutId } = useContext(LayoutContext);
    const { executeOnValueChangeAction } = useOnValueChangeAction(props);

    const value = typeof fieldName === 'string' ? useSelector(getFormFieldValue(layoutId, fieldName)) : undefined;
    const validationResult = typeof fieldName === 'string' ? useSelector(formItemValidationResult(layoutId, fieldName)) : undefined;
    const fieldMessage = typeof fieldName === 'string' ? useSelector(formFieldMessageSelector(layoutId, fieldName)) : undefined;
    const dependingFieldsValues = useSelector(formEditedFieldsSelector(layoutId, layout.dependingFields || []));

    const dispatch = useDispatch();

    useEffect(() => {
        if (typeof fieldName === 'string') {
            if (validation && typeof validation === 'object') {
                dispatch(pushFormFieldValidation(layoutId, fieldName, validation));
            }
            if (typeof fieldTitle === 'string') {
                dispatch(pushFormFieldTitle(layoutId, fieldName, fieldTitle));
            }
            dispatch(setFormFieldLayout(layoutId, fieldName, layout));
        }
    }, []);

    useEffect(() => {
        if (
            Object.values(dependingFieldsValues).length > 0
            && !(['select', 'multiple-select'].includes(layout.type))
        ) {
            onChange(null);
        }
    }, [Object.values(dependingFieldsValues).join(',')]);

    const onChange = (nextValue: any) => {
        if (typeof fieldName === 'string') {
            dispatch(setFormFieldValue(layoutId, fieldName, nextValue));
        }
        executeOnValueChangeAction(nextValue);
    };

    if (React.isValidElement(children)) {
        const { disabled, editable } = children.props;

        const additionalClassNames = [];

        if (typeof fieldMessage?.className === 'string') {
            additionalClassNames.push(`border-${fieldMessage.className.replace(/^.+-/, '')}`);
        }

        if (validationResult?.isValid === false) {
            additionalClassNames.push('is-invalid');
        }

        const clonedChildren = React.cloneElement<IFormItemProps>(children, {
            value,
            onChange,
            disabled: getIsFormItemDisabled(formMode, disabled, editable),
            additionalClassName: additionalClassNames.length > 0 ? additionalClassNames.join(' ') : undefined,
        });

        return (
            <div className="form-item">
                {typeof fieldTitle === 'string' && (
                    <label className="form-label">{fieldTitle}</label>
                )}
                {clonedChildren}
                {validationResult?.isValid === false && (
                    <div className="invalid-feedback">
                        <b>{validationResult.message}</b>
                    </div>
                )}
                {fieldMessage && (
                    <div className={`form-item-message ${fieldMessage.className}`}>
                        <b>{fieldMessage.message}</b>
                    </div>
                )}
            </div>
        );
    }

    return null;
}

export default FormItemWrapper;
